import { useRef, useState, type Dispatch, type SetStateAction } from 'react'
import { createPortal } from 'react-dom'
import toast from 'react-hot-toast'
import { Handle, NodeProps, Position } from "reactflow"
import { translations } from '../../../configs/translations'
import { AnyModuleCharacteristics, ModuleEffect, ModuleView } from '../../../models/VehicleModule'
import { VehicleModulesService } from '../../../services/VehicleModulesService'
import { useStore } from '../../../store/store'
import { ContextMenu } from '../../menus/contextMenu/ContextMenu'
import './ModuleNode.css'
import { ModuleNodeButton } from './moduleNodeButton/ModuleNodeButton'

const CONTEXT_MENU_SAFE_MARGIN = 3

let openContextMenu: { id: symbol, close: () => void } | null = null

export type ModuleNodeProps = {
	name: string,
	type: string,
	moduleKey: string,
	moduleId: number,
	vehicleId: number,
	icon: string
	button?: {
		type: 'RESEARCH' | 'BUY' | 'DONATE',
		price: number,
		onClick: () => Promise<void>
	}
	isResearched?: boolean,
	isBought?: boolean,
	isEquipped?: boolean
	onClick: () => void,
	isDonated?: boolean,
	isInModules?: boolean,
	level: number,
	setTriggerUpdate: Dispatch<SetStateAction<number>>
	onHover: (event: React.MouseEvent, data: ModuleView) => void,
	onLeave: () => void,
	otherData: {
		price: number,
		xpPrice: number,
		technicalCharacteristics: AnyModuleCharacteristics,
		effects: ModuleEffect[]
	}
}

export const ModuleNode = (props: NodeProps<ModuleNodeProps>) => {
	const {
		name,
		moduleKey,
		moduleId,
		vehicleId,
		icon,
		type,
		button,
		isDonated,
		isInModules,
		level,
		setTriggerUpdate,
		onHover,
		onLeave,
		otherData,
		isResearched,
		isBought,
		isEquipped
	} = props.data
	const [showButton, setShowButton] = useState(true)
	const language = useStore(state => state.language) as keyof typeof translations
	const t = translations[language]
	const isLoading = useRef(false)

	const [isContextMenuOpen, setContextMenuOpen] = useState(false)
	const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 })
	const contextMenuRef = useRef<HTMLDivElement>(null)
	const contextMenuId = useRef(Symbol())

	const closeContextMenu = () => {
		setContextMenuOpen(false)
		if (openContextMenu?.id === contextMenuId.current) openContextMenu = null
	}

	const handleContextMenu = (e: React.MouseEvent) => {
		e.preventDefault()
		e.stopPropagation()
		if (!isBought) return
		openContextMenu?.close()
		openContextMenu = { id: contextMenuId.current, close: closeContextMenu }
		setContextMenuPosition({ x: e.clientX, y: e.clientY })
		setContextMenuOpen(true)
		onLeave()
	}

	const handleEquip = (vehicleId: number, moduleId: number) => {
		VehicleModulesService.eqip(vehicleId, moduleId)
			.then(() => setTriggerUpdate((prev: number) => prev + 1))
			.catch((error) => {
				toast.error(
					error && error.response && error.response.data && t[error.response.data as keyof typeof t]
						? t[error.response.data as keyof typeof t]
						: t.error_unknown
				)
			})
	}


	const isDarken = () => {
		return !isBought && !button && !isDonated && !isInModules
	}

	const handleClick = async () => {
		if (isLoading.current) return
		isLoading.current = true
		if (!button) return
		const reasearchSound = new Audio('/sounds/research.mp3')
		const buySound = new Audio('/sounds/buy.mp3')

		setShowButton(false)
		await button.onClick().then(
			() => {
				setShowButton(true)
				if (button.type === 'RESEARCH') reasearchSound.play()
				if (button.type === 'BUY' || button.type === 'DONATE') buySound.play()
				setTriggerUpdate((prev: number) => prev + 1)
			}
		).catch((error) => {
			setShowButton(true)
			toast.error(
				error &&
					error.response &&
					error.response.data &&
					t[error.response.data as keyof typeof t]
					? t[error.response.data as keyof typeof t]
					: t.error_unknown,
				{ className: 'notification-error' }
			)
		}).finally(() => {
			isLoading.current = false
		})
	}

	return (
		<div
			className={`module-node ${isDarken() ? 'darken' : ''} ${isDonated ? 'donated' : ''}`}
			onClick={(e) => {
				e.stopPropagation()
				handleClick()
			}}
			onContextMenu={handleContextMenu}
			onMouseMove={(e) => {
				if (isContextMenuOpen) {
					const rect = contextMenuRef.current?.getBoundingClientRect()
					if (rect &&
						e.clientX >= rect.left - CONTEXT_MENU_SAFE_MARGIN &&
						e.clientX <= rect.right + CONTEXT_MENU_SAFE_MARGIN &&
						e.clientY >= rect.top - CONTEXT_MENU_SAFE_MARGIN &&
						e.clientY <= rect.bottom + CONTEXT_MENU_SAFE_MARGIN
					) return
					closeContextMenu()
				}
				onHover(e, {
					name,
					moduleKey,
					level,
					price: otherData.price,
					xpPrice: otherData.xpPrice,
					type: type,
					icon: icon,
					isResearched: isResearched,
					isBought: isBought,
					isEquipped: isEquipped,
					technicalCharacteristics: otherData.technicalCharacteristics,
					effects: otherData.effects
				} as ModuleView)
			}}
			onMouseLeave={onLeave}
		>
			<div className='module-node-picture'>
				<img
					className='module-node-picture-img'
					src={`/icons/modules/all/${icon}.webp`}
					onError={(e) => {
						console.log(`Cannot load image ${icon} loading ${type.toLowerCase()} instead`)
						e.currentTarget.onerror = null
						e.currentTarget.src = `/icons/modules/${type.toLowerCase()}.png`
					}}
					alt={name}
				/>
			</div>

			<div className='module-node-content'>
				<p className='module-node-name'>{name}</p>
			</div>

			<div className='car-node-button-wrapper'>
				{button && !isBought && <ModuleNodeButton
					type={button.type}
					price={button.price}
					onClick={handleClick}
					isShown={showButton}
				/>}
				{isBought && isEquipped &&
					<div className='car-node-checked'>
						<img src="/icons/24px/check.png" alt="checked" />
						<div className='car-node-checked-shadow-wrapper'>
							<div className='car-node-checked-shadow' />
						</div>
					</div>
				}
			</div>

			{isDonated &&
				<div className='car-node-donate-shadow-wrapper'>
					<div className='car-node-donate-shadow' />
				</div>
			}

			<Handle type="target" position={Position.Left} style={{ visibility: "hidden", transform: 'translateX(3px)' }} />
			<Handle type="source" position={Position.Right} style={{ visibility: "hidden", transform: 'translateX(-4px)' }} />

			{isContextMenuOpen && createPortal(
				<ContextMenu
					ref={contextMenuRef}
					isOpen={isContextMenuOpen}
					position={contextMenuPosition}
					setIsOpen={setContextMenuOpen}
					onClose={closeContextMenu}
					items={[
						<div onClick={(e) => {
							e.stopPropagation()
							closeContextMenu()
							handleEquip(vehicleId, moduleId)
						}}>
							{t.equip}
						</div>
					]}
				/>,
				document.body
			)}
		</div>
	)
}